import type { RefObject } from 'react';

import { PanelOption } from './PanelOption';
import { Search } from './Search';
import type { DropdownOption } from '../../../../types/Dropdown';
import { cn } from '../../../../utils/cn';
import { getColorClasses } from '../../../../utils/Colors';

interface PanelProps<T> {
    panelRef: RefObject<HTMLDivElement | null>;
    panelPosition: { x: number; y: number };
    panelClassName?: string;
    roundedPanel: boolean;
    scrollable: boolean;
    searchable?: boolean;
    maxPanelWidth?: number;
    minPanelWidth: number;
    keepInitialPanelWidth: boolean;
    initialPanelWidth: number | null;
    searchTerm: string;
    searchPlaceholder: string;
    onSearchChange: (term: string) => void;
    filteredOptions: DropdownOption<T>[];
    noOptionsText: string;
    multiple: boolean;
    hideCheckmarks: boolean;
    optionAlign: 'left' | 'center';
    optionClassName?: string;
    optionElementType: 'button' | 'div';
    isSelected: (value: T) => boolean;
    onSelect: (value: T) => void;
    maxHeight: number;
}

export const Panel = <T extends string | number>({
    filteredOptions,
    hideCheckmarks,
    initialPanelWidth,
    isSelected,
    keepInitialPanelWidth,
    maxHeight,
    maxPanelWidth,
    minPanelWidth,
    multiple,
    noOptionsText,
    onSearchChange,
    onSelect,
    optionAlign,
    optionClassName,
    optionElementType,
    panelClassName,
    panelPosition,
    panelRef,
    roundedPanel,
    scrollable,
    searchable,
    searchPlaceholder,
    searchTerm,
}: PanelProps<T>) => {
    return (
        <div
            ref={panelRef as RefObject<HTMLDivElement>}
            className={cn(
                'fixed z-[9999]',
                'bg-white',
                'border border-grey-outline-light border-solid',
                roundedPanel ? 'rounded-2xl' : 'rounded-md',
                'shadow-[0px_2px_6px_0px_rgba(163,177,196,0.2)]',
                'overflow-hidden',
                panelClassName,
            )}
            style={{
                contain: 'layout style paint',
                isolation: 'isolate',
                left: `${panelPosition.x}px`,
                maxHeight: scrollable ? `${maxHeight + 8 + (searchable ? 32 : 0)}px` : undefined,
                maxWidth: maxPanelWidth ? `${maxPanelWidth}px` : undefined,
                minWidth: `${(keepInitialPanelWidth && initialPanelWidth) || minPanelWidth}px`,
                top: `${panelPosition.y}px`,
                width: 'fit-content',
            }}
        >
            {/* opaque scroller (bg inherited from the panel) keeps Chrome's LCD text antialiasing while scrolling */}
            <div className="flex flex-col bg-inherit">
                {searchable && (
                    <Search
                        searchTerm={searchTerm}
                        searchPlaceholder={searchPlaceholder}
                        onSearchChange={onSearchChange}
                    />
                )}

                <div
                    className={cn('flex flex-col bg-inherit py-1', scrollable && 'overflow-y-auto')}
                    style={scrollable ? { maxHeight: `${maxHeight}px` } : undefined}
                >
                    {filteredOptions.length === 0 ? (
                        <div className="h-8 px-2 mx-1 flex items-center justify-center text-midnight text-sm">
                            {noOptionsText}
                        </div>
                    ) : (
                        filteredOptions.map((option) => (
                            <PanelOption
                                key={String(option.value)}
                                label={option.label}
                                description={option.description}
                                selected={isSelected(option.value)}
                                disabled={option.disabled}
                                multiple={multiple}
                                hideCheckmarks={hideCheckmarks}
                                optionAlign={optionAlign}
                                optionClassName={optionClassName}
                                optionElementType={optionElementType}
                                optionColorClasses={option.color ? getColorClasses(option.color) : null}
                                onSelect={() => onSelect(option.value)}
                            />
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};
